// The composition seam.
//
// Each app builds its adapters, constructs the API, and provides it here.
// Nothing in the core reaches for a module-level singleton, which is what
// makes the same code run under two different auth and storage stacks.

import React, { createContext, useContext, useMemo } from 'react';
import { createHttpClient, type PortalClientDeps } from '../client';
import { createPortalApi, type PortalApi } from '../services/index';

const PortalApiContext = createContext<PortalApi | null>(null);

export interface PortalProviderProps extends PortalClientDeps {
  children: React.ReactNode;
}

export const PortalProvider: React.FC<PortalProviderProps> = ({ children, ...deps }) => {
  // Rebuilt only when the ports themselves change. getActiveTenantId is a
  // getter rather than a value precisely so a tenant switch does not tear down
  // and recreate the client.
  const api = useMemo(
    () => createPortalApi(createHttpClient(deps)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deps.config.apiBaseUrl, deps.auth, deps.getActiveTenantId, deps.onUnauthorized]
  );

  return <PortalApiContext.Provider value={api}>{children}</PortalApiContext.Provider>;
};

export function usePortalApi(): PortalApi {
  const api = useContext(PortalApiContext);
  if (!api) {
    throw new Error('usePortalApi must be used inside a <PortalProvider>');
  }
  return api;
}
